import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { MadAuthService } from "../../mad-auth/mad-auth.service";
import { SubdomainService } from "../../_base/subdomain.service";

/** What a marker needs and nothing else. The full record is fetched when one is clicked. */
export interface ComparableMapPin {
    id: number;
    refNum: string;
    propertySubTypeId: number;
    /** null when the comparable was recorded without coordinates -- it has nowhere to sit on the map. */
    latitude: number | null;
    longitude: number | null;
    /** Land only: the parcel outline, used to place the pin when the land has no coordinates of its own. */
    landPaths: { lat: number | null, lng: number | null }[];
}

/** The columns come back as strings, and a comparable with no coordinates has them empty. */
function toNumber(value: string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = Number(value);
    return isNaN(parsed) ? null : parsed;
}

type ComparableMapPinRemote = {
    id: number,
    refNum: string,
    propertySubTypeId: number,
    latitude: string,
    longitude: string,
    land_paths?: { lat: string, lng: string }[]
};

/**
 * The markers for a comparable map page.
 *
 * This deliberately does not go through the asset class list endpoints the tables use:
 * those run every row through an API Resource that touches a dozen relations apiece, which
 * is affordable for a page of 25 rows and not for a whole viewport. The valuation-process
 * map endpoint reads the four columns a pin needs straight from the query builder
 * (`ValuationProcessComparables::mapRows`, no model hydration) while applying the very same
 * `AssetClassFilterQueryBuilder` filters as the table, so the map and the list below it stay
 * in agreement.
 */
@Injectable({
    providedIn: 'root'
})
export class ComparableMapService {
    constructor(
        private http: HttpClient,
        private authService: MadAuthService,
        private subDomainService: SubdomainService
    ) {}

    load(propertySubTypeId: number, filter: any): Observable<ComparableMapPin[]> {
        const headers = this.authService.getAuthHeaders();
        const url = environment.baseApiUrl + `api/${this.subDomainService.subDomain}/valuation-process/comparables`;
        return this.http.post<{ data: ComparableMapPinRemote[] }>(
            url,
            { filter: Object.assign({}, filter, { propertySubTypeIds: [propertySubTypeId] }) },
            { headers }
        ).pipe(
            map(response => response.data.map(item => ({
                id: item.id,
                refNum: item.refNum,
                propertySubTypeId: item.propertySubTypeId,
                latitude: toNumber(item.latitude),
                longitude: toNumber(item.longitude),
                landPaths: (item.land_paths ? item.land_paths : []).map(p => ({
                    lat: toNumber(p.lat),
                    lng: toNumber(p.lng)
                }))
            })))
        );
    }
}
